import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import useWindowWidth from "../../utils/useWindowWidth";
import { computeSidebarModes } from "../../utils/sidebarLayoutModes";

const SidebarLayoutContext = createContext(null);

export const useSidebarLayout = () => {
  const ctx = useContext(SidebarLayoutContext);
  if (!ctx) throw new Error("useSidebarLayout must be used within SidebarLayoutProvider");
  return ctx;
};

export const SidebarLayoutProvider = ({ children }) => {
  const [leftOpen, setLeftOpenState] = useState(false);
  const [activeContextTab, setActiveContextTabState] = useState(null);
  const width = useWindowWidth();
  const { pathname } = useLocation();

  const contentionEnabled = pathname.toLowerCase().startsWith("/tickets");

  // Os dois sidebars são mutuamente exclusivos: abrir um sempre fecha o outro.
  const setLeftOpen = useCallback((value) => {
    setLeftOpenState((prev) => {
      const next = typeof value === "function" ? value(prev) : value;
      if (next && !prev) setActiveContextTabState(null);
      return next;
    });
  }, []);

  const toggleLeft = useCallback(() => {
    setLeftOpen((prev) => !prev);
  }, [setLeftOpen]);

  const setActiveContextTab = useCallback((value) => {
    setActiveContextTabState((prev) => {
      const next = typeof value === "function" ? value(prev) : value;
      if (next && !prev) setLeftOpenState(false);
      return next;
    });
  }, []);

  const { leftMode, rightMode } = useMemo(
    () =>
      computeSidebarModes({
        width,
        contentionEnabled,
        leftOpen,
        rightOpen: !!activeContextTab,
      }),
    [width, contentionEnabled, leftOpen, activeContextTab]
  );

  const value = useMemo(
    () => ({
      leftOpen,
      setLeftOpen,
      toggleLeft,
      activeContextTab,
      setActiveContextTab,
      leftMode,
      rightMode,
    }),
    [leftOpen, setLeftOpen, toggleLeft, activeContextTab, setActiveContextTab, leftMode, rightMode]
  );

  return (
    <SidebarLayoutContext.Provider value={value}>
      {children}
    </SidebarLayoutContext.Provider>
  );
};
